/**
 * Extract a human-readable error message from an axios/fetch error.
 *
 * The backend (FastAPI) returns errors in two shapes:
 *   1. `detail: "Plain string message"`
 *   2. `detail: { error_code, message, ...extras }`  (used by gated endpoints)
 *
 * Rendering shape #2 directly inside JSX crashes React with error #31:
 * "Objects are not valid as a React child". This helper coerces both
 * shapes (and deeper edge cases) to a plain string suitable for
 * `setError`, `toast({description})`, and inline render.
 */
export function getErrorMessage(err, fallback = 'Something went wrong') {
  if (!err) return fallback;

  // axios error?
  const detail = err?.response?.data?.detail ?? err?.data?.detail;

  if (typeof detail === 'string' && detail) return detail;
  if (detail && typeof detail === 'object') {
    if (typeof detail.message === 'string' && detail.message) return detail.message;
    if (typeof detail.error === 'string' && detail.error) return detail.error;
  }

  // fetch / generic error
  if (typeof err?.message === 'string' && err.message) return err.message;
  if (typeof err === 'string') return err;

  return fallback;
}

/**
 * Convenience: grabs a structured error code (when available) so callers
 * can branch on `auth_required` vs `id_verification_required` etc.
 */
export function getErrorCode(err) {
  return err?.response?.data?.detail?.error_code ?? null;
}
